import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useStore } from "@/context/StoreContext";
import { PageHead, EmptyState } from "@/components/storefront/common";
import { formatINR } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import { captureAnalyticsEvent } from "@/lib/analytics";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";

export default function Cart() {
  const { cart, updateQty, removeFromCart, cartSubtotal, cartCount, autoDiscount, quoteDiscount } = useStore();
  const viewedCartRef = useRef(false);
  useEffect(() => {
    if (viewedCartRef.current) return;
    viewedCartRef.current = true;
    captureAnalyticsEvent("cart_viewed", { item_count: cartCount, currency: "INR" });
  }, [cartCount]);
  const totalMrp = cart.reduce((s, i) => s + i.mrp * i.quantity, 0);
  const savings = totalMrp - cartSubtotal;
  // Automatic discounts are recalculated by the backend whenever the cart
  // changes; when the cart no longer qualifies the row simply disappears.
  const payable = Math.max(0, cartSubtotal - quoteDiscount);

  if (cart.length === 0)
    return (
      <div className="container-x py-8">
        <PageHead title="Your Cart" crumbs={[{ label: "Home", to: "/" }, { label: "Cart" }]} />
        <EmptyState icon={ShoppingBag} title="Your cart is empty" text="Looks like you haven't added anything yet.">
          <Link to="/shop" className="inline-flex items-center gap-2 bg-slate-900 text-white font-semibold px-6 py-3 rounded-lg" data-testid="cart-shop-link">Start Shopping <ArrowRight className="h-4 w-4" /></Link>
        </EmptyState>
      </div>
    );

  return (
    <div className="container-x py-8">
      <PageHead title={`Your Cart (${cartCount})`} crumbs={[{ label: "Home", to: "/" }, { label: "Cart" }]} />
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-3" data-testid="cart-items">
          {cart.map((it) => (
            <div key={`${it.productId}-${it.variantId}`} className="flex gap-4 p-4 rounded-xl border border-slate-200 bg-white" data-testid={`cart-item-${it.slug}`}>
              <Link to={`/product/${it.slug}`} className="h-20 w-20 shrink-0 rounded-lg bg-slate-50 overflow-hidden">
                <img src={resolveMediaUrl(it.image)} alt={it.name} onError={handleMediaError} className="w-full h-full object-contain p-1" />
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`/product/${it.slug}`} className="font-semibold text-slate-900 text-sm line-clamp-2 hover:text-amber-600">{it.name}</Link>
                {it.attributes && Object.keys(it.attributes).length > 0 && (
                  <p className="text-xs text-slate-500 mt-0.5">{Object.entries(it.attributes).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>
                )}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center border border-slate-200 rounded-lg">
                    <button onClick={() => updateQty(it.productId, it.variantId, it.quantity - 1)} className="p-2" data-testid={`cart-minus-${it.slug}`}><Minus className="h-3.5 w-3.5" /></button>
                    <span className="w-8 text-center text-sm font-mono">{it.quantity}</span>
                    <button onClick={() => updateQty(it.productId, it.variantId, it.quantity + 1)} className="p-2" data-testid={`cart-plus-${it.slug}`}><Plus className="h-3.5 w-3.5" /></button>
                  </div>
                  <span className="font-mono font-semibold text-slate-900">{formatINR(it.price * it.quantity)}</span>
                </div>
              </div>
              <button onClick={() => removeFromCart(it.productId, it.variantId)} className="p-2 text-slate-400 hover:text-red-500 self-start" data-testid={`cart-remove-${it.slug}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <Link to="/shop" className="inline-block text-sm font-semibold text-amber-600 hover:text-amber-700 pt-2">← Continue shopping</Link>
        </div>

        <div className="h-fit rounded-xl border border-slate-200 bg-white p-5 lg:sticky lg:top-24" data-testid="cart-summary">
          <h3 className="font-semibold text-slate-900 mb-4">Order Summary</h3>
          <div className="space-y-2.5 text-sm">
            <Row label={`Subtotal (${cartCount} items)`} value={formatINR(cartSubtotal)} />
            {savings > 0 && <Row label="You save" value={`− ${formatINR(savings)}`} green />}
            {autoDiscount && quoteDiscount > 0 && (
              <Row label={`Discount — ${autoDiscount.name}`} value={`− ${formatINR(quoteDiscount)}`} green />
            )}
            <Row label="Shipping" value="Calculated at checkout" muted />
          </div>
          {autoDiscount && quoteDiscount > 0 && (
            <p className="text-[11px] text-emerald-600 mt-2" data-testid="cart-auto-discount-note">
              {autoDiscount.description} — applied automatically
            </p>
          )}
          <div className="border-t border-slate-200 mt-4 pt-4 flex items-center justify-between">
            <span className="font-semibold text-slate-900">Total</span>
            <span className="text-xl font-bold text-slate-900 font-mono" data-testid="cart-total">{formatINR(payable)}</span>
          </div>
          <Link to="/checkout" className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold py-3.5 rounded-lg transition" data-testid="checkout-btn">
            Proceed to Checkout <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, green, muted }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500">{label}</span>
    <span className={`font-mono ${green ? "text-emerald-600" : muted ? "text-slate-400 text-xs" : "text-slate-900"}`}>{value}</span>
  </div>
);
